import '@/lib/errorReporter';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { get, set, del } from 'idb-keyval';
import '@/index.css';
import { HomePage } from '@/pages/HomePage';
import { ViewDocPage } from '@/pages/ViewDocPage';
import { LibraryPage } from '@/pages/LibraryPage';
import { TemplatesPage } from '@/pages/TemplatesPage';
import { ArchivesPage } from '@/pages/ArchivesPage';
import { CollaboratorsPage } from '@/pages/CollaboratorsPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { HelpPage } from '@/pages/HelpPage';
import { LoginPage } from '@/pages/LoginPage';
import { SignupPage } from '@/pages/SignupPage';
import { TermsPage } from '@/pages/TermsPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { CookiesPage } from '@/pages/CookiesPage';
import { UpgradePage } from '@/pages/UpgradePage';
import { registerSW } from 'virtual:pwa-register';

// Register PWA service worker
registerSW({ immediate: true });

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      staleTime: 1000 * 60 * 5, // 5 minutes
      networkMode: 'offlineFirst',
    },
  },
});

// Custom IndexedDB persister
const idbPersister = {
  persistClient: async (client: any) => {
    await set('react-query', client);
  },
  restoreClient: async () => {
    return await get('react-query');
  },
  removeClient: async () => {
    await del('react-query');
  },
};

persistQueryClient({
  queryClient,
  persister: idbPersister as any,
  maxAge: 1000 * 60 * 60 * 24, // 24 hours
});

const router = createBrowserRouter([
  { path: "/", element: <HomePage />, errorElement: <RouteErrorBoundary /> },
  { path: "/login", element: <LoginPage />, errorElement: <RouteErrorBoundary /> },
  { path: "/signup", element: <SignupPage />, errorElement: <RouteErrorBoundary /> },
  { path: "/library", element: <LibraryPage />, errorElement: <RouteErrorBoundary /> },
  { path: "/templates", element: <TemplatesPage />, errorElement: <RouteErrorBoundary /> },
  { path: "/archives", element: <ArchivesPage />, errorElement: <RouteErrorBoundary /> },
  { path: "/collaborators", element: <CollaboratorsPage />, errorElement: <RouteErrorBoundary /> },
  { path: "/settings", element: <SettingsPage />, errorElement: <RouteErrorBoundary /> },
  { path: "/help", element: <HelpPage />, errorElement: <RouteErrorBoundary /> },
  { path: "/terms", element: <TermsPage />, errorElement: <RouteErrorBoundary /> },
  { path: "/privacy", element: <PrivacyPage />, errorElement: <RouteErrorBoundary /> },
  { path: "/cookies", element: <CookiesPage />, errorElement: <RouteErrorBoundary /> },
  { path: "/upgrade", element: <UpgradePage />, errorElement: <RouteErrorBoundary /> },
  { path: "/doc/:slug", element: <ViewDocPage />, errorElement: <RouteErrorBoundary /> },
], {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
  } as any,
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <RouterProvider router={router} future={{ v7_startTransition: true }} />
      </ErrorBoundary>
    </QueryClientProvider>
  </StrictMode>,
);